import { useKeycloak } from '@react-keycloak/web';
import { Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Grid, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { getToken, getRole } from './common';

const ProtectedRoute = ({ children }) => {
  const { keycloak, initialized } = useKeycloak();
  const location = useLocation();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const hasChecked = useRef(false);

  useEffect(() => {
    // Prevent multiple checks
    if (hasChecked.current) {
      return;
    }

    const checkAuthentication = async () => {
      // Wait for keycloak to initialize
      if (!initialized) {
        return;
      }

      // Mark as checked to prevent infinite loops
      hasChecked.current = true;

      // Check if user is authenticated
      if (!keycloak.authenticated) {
        setIsAuthorized(false);
        setIsCheckingAuth(false);
        return;
      }

      // Check if token exists in session storage
      const token = getToken();
      const role = getRole();

      // If no token or invalid role, redirect to session manager
      if (!token || token === 'undefined' || token === 'gada' || role === 'norole' || role === 'undefined') {
        setIsAuthorized(false);
        setIsCheckingAuth(false);
        return;
      }

      // User is authenticated and authorized
      setIsAuthorized(true);
      setIsCheckingAuth(false);
    };

    checkAuthentication();
  }, [keycloak.authenticated, initialized]);

  // Show loading page while checking authentication
  if (!initialized || isCheckingAuth) {
    return (
      <>
        <Helmet>
          <title>SmartShield</title>
        </Helmet>
        <Grid
          xs={12}
          item
          md={6}
          style={{
            backgroundImage: 'url(/assets/Signup.webp)',
            backgroundSize: 'cover',
            backgroundPosition: 'absolute',
            height: '100vh',
            zIndex: '1',
          }}
        >
          <div className="relative z-100 container mx-auto px-4 h-full">
            <div className="relative flex content-center items-center justify-center h-full">
              <img src="/assets/smartshild.webp" alt="smartshield Logo" className="w-100" width="200" height="200" />

              <div className="text-center mb-3">
                 <h1 className="text-center text-5xl font-bold">
  Welcome to{" "}
  <span className="
  bg-gradient-to-r
  from-[#3fbac2]
  via-[#2563eb]
  to-[#1e293b]
  bg-clip-text
  text-transparent
">
  COVAS
</span>
</h1>
                <Typography variant="h3" align="center" style={{ color: '#000000', paddingBottom: '30px' }}>
                  Checking authentication...
                </Typography>
                <Typography variant="body1" align="center" style={{ color: '#666', paddingBottom: '30px' }}>
                  Please wait while we verify your access.
                </Typography>

                <div className="w-full">
                  <Typography variant="h6" align="center" style={{ color: '#21386d', paddingTop: '30px' }}>
                    &copy; 2024 ChainSmart - All Rights Reserved
                  </Typography>
                </div>
              </div>
            </div>
            <div style={{ width: '100%' }} className="flex-auto px-4 lg:px-10 py-10 pt-0" />
          </div>
        </Grid>
      </>
    );
  }

  // If not authenticated or authorized, redirect to login
  if (!keycloak.authenticated || !isAuthorized) {
    // Check if user has no role - redirect to loading page instead
    const role = getRole();
    const token = getToken();
    if (role === 'norole' || role === 'undefined' || token === 'gada' || token === 'undefined') {
      return <Navigate to="/welcome" replace />;
    }

    // Store the current location to redirect back after login
    const currentPath = location.pathname + location.search;
    if (currentPath !== '/login' && currentPath !== '/welcome') {
      sessionStorage.setItem('redirectAfterLogin', currentPath);
    }
    return <Navigate to="/login" replace />;
  }

  // User is authenticated and authorized, render the protected content
  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node,
};

export default ProtectedRoute;
